import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {exhaustMap, of} from 'rxjs';
import {catchError, filter, map, mergeMap, switchMap, take} from 'rxjs/operators';
import * as ScreeningSettingsActions from './screening-settings.actions';
import {ScreeningSettingsService} from './screening-settings.service';
import {ScreeningSettingsDTO} from '../../dto/screening-settings';
import * as ResearchPaperActions from '../research-paper/research-paper.actions';
import {Store} from '@ngrx/store';
import {selectProjectById} from '../../../project/service/project.selectors';
import {ProjectPermissionService} from '../../../../utils/services/project-permission.service';

@Injectable()
export class ScreeningSettingEffects {
  private readonly store: Store = inject(Store);
  private readonly settingService: ScreeningSettingsService = inject(ScreeningSettingsService);
  private readonly actions$: Actions = inject(Actions);
  private readonly projectPermissionService: ProjectPermissionService = inject(ProjectPermissionService);

  loadScreeningSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScreeningSettingsActions.loadScreeningSettings),
      exhaustMap((action) =>
        this.settingService.getSetting(action.projectId).pipe(
          map((setting: ScreeningSettingsDTO) =>
            ScreeningSettingsActions.loadScreeningSettingsSuccess({projectId: action.projectId, setting})
          ),
          catchError((error) =>
            of(ScreeningSettingsActions.loadScreeningSettingsFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  handleLoadScreeningSettingsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScreeningSettingsActions.loadScreeningSettingsFailure),
      switchMap((action) =>
        this.store.select(selectProjectById(action.projectId)).pipe(
          filter(project => !!project),
          take(1),
          filter(project => this.projectPermissionService.hasWritePermission(project!)),
          map(() => ScreeningSettingsActions.generateScreeningSetting({projectId: action.projectId}))
        )
      )
    )
  );

  generateScreeningSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScreeningSettingsActions.generateScreeningSetting),
      mergeMap((action) =>
        this.settingService.generateSetting(action.projectId).pipe(
          map((setting: ScreeningSettingsDTO) =>
            ScreeningSettingsActions.generateScreeningSettingSuccess({projectId: action.projectId, setting})
          ),
          catchError((error) =>
            of(ScreeningSettingsActions.generateScreeningSettingFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  updateScreeningSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScreeningSettingsActions.updateScreeningSetting),
      mergeMap((action) =>
        this.settingService.updateSetting(action.projectId, action.id, action.setting).pipe(
          map((setting: ScreeningSettingsDTO) => {
              this.store.dispatch(ResearchPaperActions.loadResearchPapers({projectId: action.projectId}));
              return ScreeningSettingsActions.updateScreeningSettingSuccess({projectId: action.projectId, setting})
            }
          ),
          catchError((error) =>
            of(ScreeningSettingsActions.updateScreeningSettingFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );
}
